import { api } from './client';
import type { ConnectorOut } from './points';

export interface WifiCredentials {
  ssid: string;
  password: string;
}

/** Links de amenidades do ENTORNO curados pelo admin. Ausência de uma chave
 * = tile não aparece no app. Bow-owned (aspirador etc.) não entra aqui —
 * vem de `capabilities`. */
export interface AmenityLinks {
  restaurant?: string | null;
  cafe?: string | null;
  market?: string | null;
  restroom?: string | null;
  wifi?: WifiCredentials | null;
}

export interface StationOut {
  id: string;
  name: string;
  address: string;
  latitude: string | null;
  longitude: string | null;
  photo_url: string | null;
  opening_hours: string | null;
  active: boolean;
  capabilities: Record<string, unknown>;
  charger_count: number;
  amenity_links: AmenityLinks;
}

/** Estados expostos pelo Hub Gateway (OCPP-derived). `null` = Hub indisponível. */
export type ChargerState =
  | 'AVAILABLE'
  | 'PREPARING'
  | 'CHARGING'
  | 'SUSPENDED_EV'
  | 'SUSPENDED_EVSE'
  | 'FINISHING'
  | 'FAULTED'
  | 'UNAVAILABLE'
  | 'RESERVED'
  | 'OFFLINE'
  | 'UNKNOWN';

export interface StationCharger {
  id: string;
  name: string;
  hub_station_id: string;
  photo_url: string | null;
  capabilities: Record<string, unknown>;
  connectors: ConnectorOut[];
  tariff_per_kwh_brl: string | null;
  tariff_version: number | null;
  state: ChargerState | string | null;
  /** Slots OCPP do firmware. `1` = múltiplos cabos físicos compartilham slot
   * (UI colapsa pra 1 chip). >1 = cada cabo é um connector independente. */
  plugs: number | null;
  /** Mapa `evse_id -> {state, ocpp_status, ...}` quando o Hub diferencia.
   * Quando presente, cada chip pinta seu próprio state. */
  connector_states: Record<string, { state?: string; ocpp_status?: string }> | null;
  /** evse_id da sessão ativa (do StartTransaction). */
  active_evse_id: string | null;
  /** Último contato do charger com o Hub (ISO-8601). Quando mais velho que o
   * limiar do backend, `state` e os conectores já chegam `OFFLINE` — o mapa
   * `connector_states` segue cru. `null` = Hub não expõe timestamp. */
  last_seen_at?: string | null;
}

export interface StationDetailOut extends StationOut {
  chargers: StationCharger[];
}

export type AmenityClickCategory = 'restaurant' | 'cafe' | 'market' | 'restroom' | 'wifi';

/** Distribuição de sessões por hora do dia (horário de São Paulo), últimos
 * 90 dias — dado real (não mais o mock fixo do design original). `hours`
 * sempre tem 24 posições (índice = hora 0-23). */
export interface PeakHoursOut {
  hours: number[];
  sample_size: number;
}

export const stationsApi = {
  list: () => api.get<StationOut[]>('/stations'),
  /** Lista com chargers/connectors/tariff/states embedded — pra renderizar
   * cards com badge de "X livres" + tipos de carregador sem N+1 GET. */
  listDetailed: () => api.get<StationDetailOut[]>('/stations?detail=true'),
  get: (stationId: string) => api.get<StationDetailOut>(`/stations/${stationId}`),
  /** Métrica de engajamento (feedback Saulo 2026-07-06) — fire-and-forget,
   * ver `trackAmenityClick` em `lib/`. Não bloqueia nem falha visivelmente. */
  trackAmenityClick: (stationId: string, category: AmenityClickCategory) =>
    api.post<void>(`/stations/${stationId}/amenity-clicks`, { category }),
  peakHours: (stationId: string) => api.get<PeakHoursOut>(`/stations/${stationId}/peak-hours`)
};
